/**
 * Bulk-selection bar for assigned post-it templates.
 */
import * as React from 'react';
import { Button } from '@/components/forms/Button';
import { Select } from '@/components/forms/Select';
import { RotateCcw, CheckSquare, Square, Trash2, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { getAllowedTargetRoles } from '@/services/post-its.service';
import { useMode } from '@/contexts/ModeContext';
import type { AssignedRecurrence, ScheduleConfig } from '@/types/post-it.types';
import {
  buildSelectionActionConfig,
  isSelectionApplyEnabled,
  type SelectionActionConfig,
} from '@/components/post-its/post-it-form-utils';
import { ScheduleConfigForm } from './schedule/ScheduleConfigForm';

/** Props for {@link PostItsPlanningSelectionBar}. */
interface PostItsPlanningSelectionBarProps {
  selectedIds: string[];
  totalCount: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onApply: (config: SelectionActionConfig) => void;
  onDelete?: () => void;
  onSetPriority?: () => void;
  isLoading?: boolean;
  className?: string;
}

/** Bulk-selection bar for assigned post-it templates. */
export const PostItsPlanningSelectionBar: React.FC<PostItsPlanningSelectionBarProps> = ({
  selectedIds,
  totalCount,
  onSelectAll,
  onClearSelection,
  onApply,
  onDelete,
  onSetPriority,
  isLoading = false,
  className,
}) => {
  const [roleId, setRoleId] = React.useState('');
  const [recurrence, setRecurrence] = React.useState<AssignedRecurrence | ''>('');
  const [specificDateFrom, setSpecificDateFrom] = React.useState('');
  const [specificDateTo, setSpecificDateTo] = React.useState('');
  const [quarterlyPeriods, setQuarterlyPeriods] = React.useState<number[]>([]);
  const [scheduleConfig, setScheduleConfig] = React.useState<ScheduleConfig | null>(null);

  const { mode: appMode } = useMode();

  // Same rule as the modal: admins/volunteers + mode from in-person / remote (BE)
  const { data: allowedRoles } = useQuery({
    queryKey: ['allowed-target-roles', appMode],
    queryFn: () => getAllowedTargetRoles(appMode ?? undefined),
    enabled: !!appMode,
  });

  const roleOptions = React.useMemo(
    () => [
      { value: '', label: 'Choisir un rôle' },
      ...(allowedRoles ?? []).map((r) => ({ value: r.id, label: r.label })),
    ],
    [allowedRoles],
  );

  const hasSelection = selectedIds.length > 0;
  const allSelected = totalCount > 0 && selectedIds.length === totalCount;
  const isApplyEnabled = isSelectionApplyEnabled(
    hasSelection,
    roleId,
    scheduleConfig,
    recurrence,
    specificDateFrom,
  );

  /** Select every visible template, or clear if all are already selected. */
  const handleToggleAll = () => {
    if (allSelected) {
      onClearSelection();
    } else {
      onSelectAll();
    }
  };

  const handleApply = () => {
    if (!isApplyEnabled) return;

    onApply(
      buildSelectionActionConfig(
        roleId,
        scheduleConfig,
        recurrence,
        specificDateFrom,
        specificDateTo,
        quarterlyPeriods,
      ),
    );
    setRoleId('');
    setRecurrence('');
    setSpecificDateFrom('');
    setSpecificDateTo('');
    setQuarterlyPeriods([]);
    setScheduleConfig(null);
  };

  /** Clear selection and local role / schedule draft. */
  const handleReset = () => {
    onClearSelection();
    setRoleId('');
    setRecurrence('');
    setSpecificDateFrom('');
    setSpecificDateTo('');
    setQuarterlyPeriods([]);
    setScheduleConfig(null);
  };

  return (
    <div className={cn('filters-panel mb-5', className)}>
      <div className="filter-panel-header">
        <div className="filter-groups-container">
          <div className="filter-group">
            <span className="filter-group-label">Sélection</span>
            <div className="filter-options items-center">
              <Button
                variant="secondary"
                size="sm"
                icon={allSelected ? CheckSquare : Square}
                iconPosition="left"
                onClick={handleToggleAll}
                disabled={totalCount === 0}
                className={cn(allSelected && 'section-action-button-active')}
              >
                Tout sélectionner
              </Button>
            </div>
          </div>

          <div className="filter-group">
            <span className="filter-group-label">Appliquer à la sélection</span>
            <div className="flex flex-wrap gap-3">
              <div className="min-w-[200px]">
                <Select
                  placeholder="Choisir un rôle"
                  options={roleOptions}
                  value={roleId}
                  onChange={setRoleId}
                  size="sm"
                />
              </div>
              <div className="flex-1">
                <ScheduleConfigForm
                  value={scheduleConfig}
                  onChange={(config) => setScheduleConfig(config)}
                  color="blanc-casse"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {(onDelete || onSetPriority) && selectedIds.length > 0 && (
        <div className="filter-group">
          <span className="filter-group-label">Actions groupées</span>
          <div className="flex gap-2">
            {onDelete && (
              <Button
                variant="danger"
                size="sm"
                icon={Trash2}
                iconPosition="left"
                onClick={onDelete}
                loading={isLoading}
                disabled={isLoading}
              >
                Supprimer
              </Button>
            )}
            {onSetPriority && (
              <Button
                variant="secondary"
                size="sm"
                icon={Star}
                iconPosition="left"
                onClick={onSetPriority}
                loading={isLoading}
                disabled={isLoading}
              >
                Marquer comme prioritaire
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="filter-actions">
        <span className="filter-result-count">
          {selectedIds.length} post-it{selectedIds.length !== 1 ? 's' : ''} sélectionné
          {selectedIds.length !== 1 ? 's' : ''}
        </span>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={RotateCcw}
            iconPosition="left"
            onClick={handleReset}
            disabled={!hasSelection && !roleId && !recurrence && !scheduleConfig}
          >
            Réinitialiser
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleApply}
            disabled={!isApplyEnabled || isLoading}
            loading={isLoading}
          >
            Appliquer
          </Button>
        </div>
      </div>
    </div>
  );
};
